import { jsxs, jsx } from "react/jsx-runtime";
import { useReducer, useRef, useEffect } from "react";
import { useLayer, mergeRefs } from "react-laag";
import { focusSafely, FocusScope } from "@react-aria/focus";
import { useId } from "./useId.js";
import { Arrow } from "./Arrow2.js";
import { Container } from "./Container2.js";
import { getUseLayerOptions } from "./getUseLayerOptions2.js";
import { reducer, initialState } from "./state3.js";
import { getFirstShadowElement, isInShadow } from "./shadowDom3.js";
import { fixLayerProps } from "./fixLayerProps.js";
import { useKeyDown } from "./useKeyDown.js";
function Popover({
  children,
  trigger,
  position = "bottom-left",
  onOpen,
  onClose,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  noPadding,
  ...props
}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const triggerRef = useRef();
  const containerRef = useRef(null);
  const initialRender = useRef(true);
  const popoverId = useId("popover");
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (state.isOpen) {
      onOpen == null ? void 0 : onOpen();
    } else {
      onClose == null ? void 0 : onClose();
    }
  }, [state.isOpen]);
  useKeyDown("Escape", () => {
    if (state.isOpen) {
      dispatch({
        type: "close"
      });
    }
  });
  const {
    renderLayer,
    triggerProps: {
      ref: triggerLayerRef
    },
    layerProps,
    triggerBounds
  } = useLayer({
    isOpen: state.isOpen,
    onOutsideClick() {
      if (isInShadow(triggerRef.current)) {
        return;
      }
      dispatch({
        type: "close"
      });
    },
    onParentClose() {
      dispatch({
        type: "close"
      });
    },
    onDisappear: (type) => {
      if (type === "full") {
        dispatch({
          type: "close"
        });
      }
    },
    triggerOffset: 4,
    containerOffset: 16,
    auto: true,
    container: getFirstShadowElement(triggerRef.current),
    ...getUseLayerOptions(position)
  });
  fixLayerProps(layerProps);
  const containerWidth = position === "bottom" ? {
    width: triggerBounds == null ? void 0 : triggerBounds.width,
    minWidth: "initial",
    maxWidth: "initial"
  } : {};
  function onTriggerKeyDown(event) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      dispatch({
        type: "toggle"
      });
    }
  }
  useEffect(() => {
    if (state.isOpen) {
      if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
        focusSafely(containerRef.current);
      }
    }
  }, [state.isOpen]);
  return /* @__PURE__ */ jsxs("div", {
    ...props,
    children: [trigger({
      isOpen: state.isOpen,
      onClick: () => {
        dispatch({
          type: "toggle"
        });
      },
      "aria-expanded": state.isOpen,
      "aria-controls": state.isOpen ? popoverId : void 0,
      onKeyDown: onTriggerKeyDown,
      ref: mergeRefs(triggerLayerRef, triggerRef)
    }), state.isOpen && renderLayer(/* @__PURE__ */ jsx(FocusScope, {
      contain: true,
      restoreFocus: true,
      children: /* @__PURE__ */ jsx(Container, {
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        id: popoverId,
        ...layerProps,
        ref: mergeRefs(layerProps.ref, containerRef),
        style: {
          ...layerProps.style,
          ...containerWidth
        },
        noPadding,
        children
      })
    }))]
  });
}
Popover.Arrow = Arrow;
export {
  Popover
};
//# sourceMappingURL=Popover.js.map
